import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  Building2,
  TrendingUp,
  DollarSign,
  ChevronRight,
  ArrowLeft,
  AlertCircle,
  RefreshCw,
  SlidersHorizontal,
  X,
  Download,
  Wallet,
  Landmark,
} from "lucide-react";
import api from "../../services/api";
import { getTokenInfo } from "../../utils/auth";
import UserDetailsModal, { LoyaltyCustomer } from "../../components/UserDetailsModal";
import { toast } from "react-hot-toast";
import { downloadFile } from "../../utils/download";
import { useNavigate } from "react-router-dom";
import type { AdminBalanceData } from "./AdminBalance";

// ─── Types ───────────────────────────────────────────────────────────────────

interface RestaurantSummary {
  restaurantId: string;
  hotelName: string;
  address: string;
  // All-time figures, same as the cards on the restaurant's own ledger
  totalOrders?: number;
  orderRevenue?: number;
  totalDebit: number;
  totalCredit: number;
  // Card figures: payouts to the restaurant count as settled, not as bonuses
  bonusesIssued?: number;
  bonusesEarned?: number;
  settled?: number;
  balance: number;
  lastEntry?: string | null;
}

interface LedgerEntry {
  _id: string;
  type: "order" | "bonus" | "settlement" | "adjustment" | "wallet_spend" | "order_cancelled";
  direction: "debit" | "credit";
  amount: number;
  description: string;
  adminNote?: string;
  createdAt: string;
  userId?: {
    _id: string;
    name: string;
    phonenumber: string;
    email: string;
    // Customer's current balances (not at the time of this entry)
    wallet?: { balance?: number };
    bonusWallet?: { balance?: number };
  };
  // Bonus entries: order total and loyalty tier % applied
  orderAmount?: number;
  loyaltyPercent?: number;
  orderId?: string;
  // wallet_spend entries: which customer wallet paid
  walletType?: "wallet" | "bonus_wallet";
  // Restaurant's all-time balance right after this entry (positive = owes platform)
  runningBalance?: number | null;
  // Recent entries across all restaurants
  restaurantName?: string;
}

interface LedgerData {
  entries: LedgerEntry[];
  total: number;
  page: number;
  pages: number;
  ordersSummary: { totalAmount: number; count: number };
  periodSummary: {
    orderRevenue: number;
    orderCount: number;
    loyaltyDebits: number;
    bonusesIssued?: number;
    bonusesEarned?: number;
    walletSpendCredits: number;
    totalDebit: number;
    totalCredit: number;
    settled?: number;
    balance: number;
  };
  allTimeSummary: {
    orderRevenue: number;
    orderCount: number;
    loyaltyDebits: number;
    bonusesIssued?: number;
    bonusesEarned?: number;
    walletSpendCredits: number;
    totalDebit: number;
    totalCredit: number;
    settled?: number;
    balance: number;
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (n: number, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(n);

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const typeConfig: Record<
  string,
  { label: string; color: string; bg: string }
> = {
  order: { label: "Order", color: "text-blue-700", bg: "bg-blue-100" },
  bonus: { label: "Bonus", color: "text-purple-700", bg: "bg-purple-100" },
  settlement: { label: "Settlement", color: "text-green-700", bg: "bg-green-100" },
  adjustment: { label: "Adjustment", color: "text-orange-700", bg: "bg-orange-100" },
  wallet_spend: { label: "Wallet Payment", color: "text-teal-700", bg: "bg-teal-100" },
  bonus_wallet_spend: { label: "Bonus Wallet Payment", color: "text-pink-700", bg: "bg-pink-100" },
  order_cancelled: { label: "Order Cancelled", color: "text-red-700", bg: "bg-red-100" },
};

const typeConfigFor = (entry: LedgerEntry) =>
  (entry.type === "wallet_spend" && entry.walletType === "bonus_wallet"
    ? typeConfig.bonus_wallet_spend
    : typeConfig[entry.type]) || typeConfig.adjustment;

/**
 * Red means the balance is against whoever is viewing: a location admin sees red when their
 * restaurant owes the platform; a platform admin sees red when the platform owes the restaurant.
 */
const balanceIsBad = (balance: number) =>
  getTokenInfo()?.role === "location_admin" ? balance > 0 : balance < 0;

/** Title for the balance card: says who owes whom. */
const balanceTitle = (balance: number) =>
  balance > 0 ? "Restaurant Owes Platform" : balance < 0 ? "Platform Owes Restaurant" : "Outstanding Balance";

// ─── Ledger entry row (restaurant ledger + recent entries) ───────────────────

const LedgerEntryRow: React.FC<{
  entry: LedgerEntry;
  currency: string;
  showLocation?: boolean;
  onOpenUser?: (userId: string) => void;
  openingUserId?: string | null;
}> = ({ entry, currency, showLocation = false, onOpenUser, openingUserId }) => {
  const tc = typeConfigFor(entry);
  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-6 py-4 text-gray-500 whitespace-nowrap">
        {fmtDate(entry.createdAt)}
      </td>
      {showLocation && (
        <td className="px-6 py-4 text-gray-700">{entry.restaurantName}</td>
      )}
      <td className="px-6 py-4">
        <span
          className={`inline-flex items-center justify-center text-center px-2 py-0.5 rounded-full text-xs font-medium ${tc.bg} ${tc.color}`}
        >
          {tc.label}
        </span>
      </td>
      <td className="px-6 py-4 text-gray-800">
        <p>
          {entry.type === "bonus" && entry.orderAmount != null
            ? entry.description.replace(
                /(on order #\S+)/,
                (match) => `${match} (${fmt(entry.orderAmount!, currency)})`
              )
            : entry.description}
        </p>
        {entry.type === "bonus" && entry.loyaltyPercent != null && (
          <p className="text-xs text-gray-500 mt-0.5">
            Loyalty applied:{" "}
            <span className="font-semibold text-purple-700">
              {entry.loyaltyPercent}%
            </span>
          </p>
        )}
        {entry.adminNote && (
          <p className="text-xs text-gray-400 mt-0.5">Note: {entry.adminNote}</p>
        )}
        {entry.userId && (
          <p className="text-xs text-gray-400 mt-0.5">
            User:{" "}
            {onOpenUser ? (
              <button
                type="button"
                onClick={() => onOpenUser(entry.userId!._id)}
                disabled={openingUserId === entry.userId._id}
                className="text-blue-600 hover:text-blue-800 hover:underline font-medium disabled:opacity-60"
              >
                {entry.userId.name || entry.userId.phonenumber}
              </button>
            ) : (
              <span className="font-medium text-gray-700">
                {entry.userId.name || entry.userId.phonenumber}
              </span>
            )}
          </p>
        )}
      </td>
      <td className="px-6 py-4 text-right font-medium whitespace-nowrap">
        {entry.amount === 0 ? (
          // Informational (e.g. card refund on a cancelled order) — no balance change
          <span className="text-gray-400">—</span>
        ) : (
        <span
          className={`inline-flex items-center justify-end whitespace-nowrap tabular-nums ${
            entry.direction === "debit" ? "text-red-600" : "text-green-600"
          }`}
        >
          {`${entry.direction === "debit" ? "+" : "-"}${fmt(entry.amount, currency)}`}
        </span>
        )}
        {entry.runningBalance != null && (
          <p
            className={`mt-1 text-xs font-normal tabular-nums ${
              entry.runningBalance === 0
                ? "text-gray-400"
                : balanceIsBad(entry.runningBalance)
                  ? "text-red-500"
                  : "text-green-600"
            }`}
          >
            Bal:{" "}
            {entry.runningBalance > 0
              ? "Owes "
              : entry.runningBalance < 0
                ? "Credit "
                : ""}
            {fmt(Math.abs(entry.runningBalance), currency)}
          </p>
        )}
      </td>
      <td className="px-6 py-4 text-right">
        {entry.amount === 0 ? (
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
            Info
          </span>
        ) : (
        <span
          className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            entry.direction === "debit"
              ? "bg-red-100 text-red-700"
              : "bg-green-100 text-green-700"
          }`}
        >
          {entry.direction === "debit" ? "Owed" : "Paid"}
        </span>
        )}
      </td>
    </tr>
  );
};

// ─── Summary View ─────────────────────────────────────────────────────────────

const SummaryView: React.FC<{
  summaries: RestaurantSummary[];
  loading: boolean;
  onSelect: (r: RestaurantSummary) => void;
  onRefresh: () => void;
}> = ({ summaries, loading, onSelect, onRefresh }) => {
  const { currency } = useAuth();
  const sum = (pick: (r: RestaurantSummary) => number | undefined) =>
    summaries.reduce((s, r) => s + (pick(r) ?? 0), 0);
  const totalRevenue = sum((r) => r.orderRevenue);
  const totalOrders = sum((r) => r.totalOrders);
  const totalBonuses = sum((r) => r.bonusesIssued);
  const totalEarned = sum((r) => r.bonusesEarned);
  const netBalance = sum((r) => r.balance);
  const debtors = summaries.filter((r) => r.balance > 0).length;

  // Customer wallet top-ups (platform-wide); refreshed with the summaries
  const [recharges, setRecharges] = useState<{
    total: number;
    count: number;
    spent: number;
    outstanding: number;
  } | null>(null);
  const [adminBalance, setAdminBalance] = useState<AdminBalanceData | null>(null);
  const navigate = useNavigate();
  useEffect(() => {
    api
      .get("/ledger/wallet-recharges")
      .then((res) => {
        if (res.data.success) setRecharges(res.data.data);
      })
      .catch(() => setRecharges(null));
    api
      .get("/ledger/admin-balance")
      .then((res) => {
        if (res.data.success) setAdminBalance(res.data.data);
      })
      .catch(() => setAdminBalance(null));
  }, [summaries]);

  const balanceLabel = (balance: number) =>
    `${balance > 0 ? "Owes " : balance < 0 ? "Credit " : ""}${fmt(Math.abs(balance), currency)}`;
  const balanceColor = (balance: number) =>
    balance === 0 ? "text-gray-500" : balanceIsBad(balance) ? "text-red-600" : "text-green-600";

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Restaurant Ledger</h1>
          <p className="text-sm text-gray-500 mt-1">
            Track cash flow & settle balances · {summaries.length} locations
            {debtors > 0 ? ` · ${debtors} with outstanding balance` : ""}
          </p>
        </div>
        <button
          onClick={onRefresh}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Left: all-location totals in a 2×2 grid · Right: per-location table */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6 items-start">
      {/* All-location totals (same cards as a single restaurant's ledger) */}
      <div className="xl:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-blue-500" />
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide m-0">Order Revenue</p>
          </div>
          <p className="text-2xl font-bold text-blue-700">{fmt(totalRevenue, currency)}</p>
          <p className="text-xs text-gray-400 mt-1">{totalOrders} orders · all locations</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-indigo-500" />
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide m-0">Bonuses Earned</p>
          </div>
          <p className="text-2xl font-bold text-indigo-600">{fmt(totalEarned, currency)}</p>
          <p className="text-xs text-gray-400 mt-1">Loyalty on orders</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-purple-500" />
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide m-0">Bonuses Issued</p>
          </div>
          <p className="text-2xl font-bold text-purple-600">{fmt(totalBonuses, currency)}</p>
          <p className="text-xs text-gray-400 mt-1">Compensation</p>
        </div>
        <div
          className={`rounded-xl border p-5 shadow-sm ${
            balanceIsBad(netBalance) ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className={`w-4 h-4 ${balanceIsBad(netBalance) ? "text-red-500" : "text-green-500"}`} />
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 m-0">
              {balanceTitle(netBalance)}
            </p>
          </div>
          <p className={`text-2xl font-bold ${balanceIsBad(netBalance) ? "text-red-700" : "text-green-700"}`}>
            {balanceLabel(netBalance)}
          </p>
          <p className="text-xs text-gray-400 mt-1">Net across all locations</p>
        </div>
        {/* Platform's own balance; whole card opens the Admin Balance page */}
        <button
          type="button"
          onClick={() => navigate("/admin/ledger/admin-balance")}
          className={`sm:col-span-2 text-left rounded-xl border p-5 shadow-sm cursor-pointer transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-400 ${
            (adminBalance?.balance ?? 0) >= 0
              ? "bg-emerald-50 border-emerald-200 hover:border-emerald-300"
              : "bg-red-50 border-red-200 hover:border-red-300"
          }`}
        >
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2">
              <Landmark className="w-4 h-4 text-emerald-600" />
              <p className="text-xs text-gray-600 font-medium uppercase tracking-wide m-0">Admin Balance</p>
            </div>
            <span className="flex items-center gap-1 text-xs font-medium text-emerald-700">
              View logs <ChevronRight className="w-4 h-4" />
            </span>
          </div>
          <p
            className={`text-2xl font-bold m-0 ${(adminBalance?.balance ?? 0) >= 0 ? "text-emerald-700" : "text-red-700"}`}
          >
            {adminBalance
              ? `${adminBalance.balance < 0 ? "−" : adminBalance.balance > 0 ? "+" : ""}${fmt(Math.abs(adminBalance.balance), currency)}`
              : "—"}
          </p>
          {adminBalance && (
            <p className="text-xs text-gray-500 mt-1 mb-0">
              Customer money held {fmt(adminBalance.customersHeld.total, currency)} · restaurants{" "}
              {adminBalance.restaurants.net >= 0 ? "owe " : "are owed "}
              {fmt(Math.abs(adminBalance.restaurants.net), currency)} · issued by admin{" "}
              {fmt(adminBalance.issuedByAdmin.total, currency)}
            </p>
          )}
        </button>
        {/* Whole card opens the Wallet Recharge Report page */}
        <button
          type="button"
          onClick={() => navigate("/admin/ledger/wallet-recharges")}
          className="sm:col-span-2 text-left bg-white rounded-xl border border-gray-200 p-5 shadow-sm cursor-pointer transition hover:border-teal-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-teal-400"
        >
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2">
              <Wallet className="w-4 h-4 text-teal-500" />
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide m-0">Wallet Recharge</p>
            </div>
            <span className="flex items-center gap-1 text-xs font-medium text-teal-700">
              View report <ChevronRight className="w-4 h-4" />
            </span>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <div>
              <p className="text-2xl font-bold text-teal-700 m-0">{fmt(recharges?.total ?? 0, currency)}</p>
              <p className="text-xs text-gray-400 mt-1 m-0">
                Received{recharges ? ` · ${recharges.count} top-ups` : ""}
              </p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-700 m-0">{fmt(recharges?.spent ?? 0, currency)}</p>
              <p className="text-xs text-gray-400 mt-1 m-0">Spent on orders</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600 m-0">{fmt(recharges?.outstanding ?? 0, currency)}</p>
              <p className="text-xs text-gray-400 mt-1 m-0">Still owed to customers</p>
            </div>
          </div>
        </button>
      </div>

      {/* Per-location breakdown */}
      <div className="xl:col-span-3 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-800">All Restaurants</h2>
        </div>
        {loading ? (
          <div className="p-12 text-center text-gray-400">Loading...</div>
        ) : summaries.length === 0 ? (
          <div className="p-12 text-center text-gray-400">No restaurants found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-gray-500">
                  <th className="text-left px-6 py-3 font-medium">Location</th>
                  <th className="text-right px-4 py-3 font-medium">Order Revenue</th>
                  <th className="text-right px-4 py-3 font-medium">Bonuses Earned</th>
                  <th className="text-right px-4 py-3 font-medium">Bonuses Issued By Locations</th>
                  <th className="text-right px-4 py-3 font-medium">Outstanding Balance</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {summaries.map((r) => (
                  <tr
                    key={r.restaurantId}
                    onClick={() => onSelect(r)}
                    className="cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{r.hotelName}</p>
                          <p className="text-xs text-gray-500">{r.address}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right whitespace-nowrap">
                      <p className="font-semibold text-blue-700 tabular-nums">{fmt(r.orderRevenue ?? 0, currency)}</p>
                      <p className="text-xs text-gray-400">{r.totalOrders ?? 0} orders</p>
                    </td>
                    <td className="px-4 py-4 text-right whitespace-nowrap font-semibold text-indigo-600 tabular-nums">
                      {fmt(r.bonusesEarned ?? 0, currency)}
                    </td>
                    <td className="px-4 py-4 text-right whitespace-nowrap font-semibold text-purple-600 tabular-nums">
                      {fmt(r.bonusesIssued ?? 0, currency)}
                    </td>
                    <td className={`px-4 py-4 text-right whitespace-nowrap font-semibold tabular-nums ${balanceColor(r.balance)}`}>
                      {balanceLabel(r.balance)}
                    </td>
                    <td className="pr-4 text-right">
                      <ChevronRight className="w-4 h-4 text-gray-400 inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold">
                  <td className="px-6 py-3 text-gray-900">All locations</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap text-blue-700 tabular-nums">
                    {fmt(totalRevenue, currency)}
                    <span className="block text-xs font-normal text-gray-400">{totalOrders} orders</span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap text-indigo-600 tabular-nums">{fmt(totalEarned, currency)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap text-purple-600 tabular-nums">{fmt(totalBonuses, currency)}</td>
                  <td className={`px-4 py-3 text-right whitespace-nowrap tabular-nums ${balanceColor(netBalance)}`}>
                    {balanceLabel(netBalance)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
      </div>

      <RecentEntries />
    </div>
  );
};

// ─── Wallet recharge report ──────────────────────────────────────────────────

interface WalletReportEntry {
  _id: string;
  createdAt: string;
  type: "credit" | "debit";
  source?: string;
  /** "" for gift cards bought by Square (no wallet involved) */
  walletType: "wallet" | "";
  activity: string;
  description?: string;
  amount: number;
  balanceBefore: number | null;
  balanceAfter: number | null;
  transactionId: string;
  orderNumber: string;
  location: string;
  customer: {
    name: string;
    phonenumber: string;
    email: string;
    walletBalance: number | null;
  } | null;
}

type WalletView = "all" | "recharges" | "spends" | "giftcards";
const WALLET_VIEW_TITLES: Record<WalletView, string> = {
  all: "Wallet Transactions",
  recharges: "Wallet Recharges",
  spends: "Wallet Spends",
  giftcards: "Gift Cards",
};

interface GiftCardPerson {
  name: string;
  phonenumber: string;
  email: string;
}

interface GiftCardRow {
  _id: string;
  createdAt: string;
  amount: number;
  paidWith: "Wallet" | "Square";
  transactionId: string;
  status: "redeemed" | "unredeemed" | "expired";
  expiresAt: string;
  redeemedAt: string | null;
  buyer: GiftCardPerson | null;
  redeemer: GiftCardPerson | null;
}

const GIFT_STATUS: Record<GiftCardRow["status"], { label: string; className: string }> = {
  redeemed: { label: "Redeemed", className: "bg-gray-100 text-gray-700" },
  unredeemed: { label: "Not redeemed yet", className: "bg-amber-50 text-amber-700" },
  expired: { label: "Expired", className: "bg-red-50 text-red-700" },
};

const personLine = (p: GiftCardPerson | null) =>
  p ? (
    <>
      <p className="m-0 font-medium text-gray-900">{p.name || "—"}</p>
      <p className="m-0 text-xs text-gray-500">{[p.phonenumber, p.email].filter(Boolean).join(" · ")}</p>
    </>
  ) : (
    <span className="text-gray-400">—</span>
  );

const WalletRechargeReport: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const { currency } = useAuth();
  const toLocalDateStr = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return toLocalDateStr(d);
  });
  const [endDate, setEndDate] = useState(() => toLocalDateStr(new Date()));
  const [view, setView] = useState<WalletView>("all");
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState<{
    entries: WalletReportEntry[];
    total: number;
    page: number;
    pages: number;
    summary: { credits: number; debits: number; count: number; customers: number };
    giftCards?: GiftCardRow[];
    giftSummary?: {
      count: number;
      sold: number;
      paidByWallet: number;
      paidBySquare: number;
      paidBySquareCount: number;
      redeemed: number;
      redeemedCount: number;
      unredeemed: number;
      unredeemedCount: number;
      expired: number;
      expiredCount: number;
    };
    flows: {
      recharged: number;
      rechargeCount: number;
      spent: number;
      spentCount: number;
      refunded: number;
      otherIn: number;
      otherOut: number;
    };
    outstanding: {
      wallet: number;
      walletCustomers: number;
      giftCards: number;
      giftCardCount: number;
      expiredGiftCards: number;
      expiredGiftCardCount: number;
      total: number;
      bonus: number;
    };
  } | null>(null);

  const filterParams = useCallback(() => {
    const params: Record<string, string> = { view };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return params;
  }, [startDate, endDate, view]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/ledger/wallet-recharges/report", { params: { ...filterParams(), page, limit: RECENT_PAGE_SIZE } })
      .then((res) => {
        if (!cancelled && res.data.success) setData(res.data.data);
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load wallet report");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, filterParams, reloadKey]);

  const handleExport = async () => {
    try {
      setExporting(true);
      await downloadFile(
        "/ledger/wallet-recharges/export",
        filterParams(),
        `${view === "giftcards" ? "gift-cards" : `wallet-${view === "all" ? "transactions" : view}`}-${startDate || "all"}-to-${endDate || "today"}.xlsx`
      );
    } catch {
      toast.error("Failed to export wallet report");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-teal-200 p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-gray-800 flex items-center gap-2 m-0">
            <Wallet className="w-4 h-4 text-teal-500" />
            {WALLET_VIEW_TITLES[view]} Report
          </h2>
          {onClose && (
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg" aria-label="Close report">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Show</label>
            <select
              value={view}
              onChange={(e) => { setView(e.target.value as WalletView); setPage(1); }}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All wallet transactions</option>
              <option value="recharges">Recharges only</option>
              <option value="spends">Spent on orders only</option>
              <option value="giftcards">Gift cards</option>
            </select>
          </div>
          <button
            onClick={() => { setPage(1); setReloadKey((k) => k + 1); }}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Apply
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            {exporting ? "Exporting..." : "Download Excel"}
          </button>
        </div>
        {data?.giftSummary && view === "giftcards" && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-6 text-sm">
            <span className="text-gray-500">
              Sold: <span className="font-semibold text-gray-900">{fmt(data.giftSummary.sold, currency)}</span>
              <span className="text-gray-400"> ({data.giftSummary.count} cards)</span>
            </span>
            <span className="text-gray-500">
              Paid by Square:{" "}
              <span className="font-semibold text-teal-700">{fmt(data.giftSummary.paidBySquare, currency)}</span>
            </span>
            <span className="text-gray-500">
              Paid with wallet:{" "}
              <span className="font-semibold text-gray-800">{fmt(data.giftSummary.paidByWallet, currency)}</span>
            </span>
            <span className="text-gray-500">
              Redeemed: <span className="font-semibold text-gray-800">{fmt(data.giftSummary.redeemed, currency)}</span>
            </span>
            <span className="text-gray-500">
              Not redeemed yet:{" "}
              <span className="font-semibold text-amber-700">{fmt(data.giftSummary.unredeemed, currency)}</span>
            </span>
            {data.giftSummary.expiredCount > 0 && (
              <span className="text-gray-500">
                Expired: <span className="font-semibold text-red-600">{fmt(data.giftSummary.expired, currency)}</span>
              </span>
            )}
          </div>
        )}
        {data && view !== "giftcards" && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-6 text-sm">
            <span className="text-gray-500">
              {view === "recharges" ? "Recharged:" : "Credited:"}{" "}
              <span className="font-semibold text-teal-700">{fmt(data.summary.credits, currency)}</span>
              <span className="text-gray-400">
                {" "}({data.summary.count} {view === "recharges" ? "top-ups" : "entries"})
              </span>
            </span>
            {view !== "recharges" && (
              <span className="text-gray-500">
                Debited: <span className="font-semibold text-red-600">{fmt(data.summary.debits, currency)}</span>
              </span>
            )}
            <span className="text-gray-500">
              Customers: <span className="font-semibold text-gray-800">{data.summary.customers}</span>
            </span>
          </div>
        )}
      </div>

      {data?.flows && data.outstanding && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Main wallet money in the selected period */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide m-0 mb-3">
              Main wallet · selected period
            </p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">
                  Received from customers{" "}
                  <span className="text-gray-400">({data.flows.rechargeCount} top-ups)</span>
                </span>
                <span className="font-semibold text-teal-700 tabular-nums">+{fmt(data.flows.recharged, currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">
                  Spent on orders <span className="text-gray-400">({data.flows.spentCount} orders)</span>
                </span>
                <span className="font-semibold text-red-600 tabular-nums">-{fmt(data.flows.spent, currency)}</span>
              </div>
              {data.flows.refunded > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Refunded back to wallet</span>
                  <span className="font-medium text-teal-700 tabular-nums">+{fmt(data.flows.refunded, currency)}</span>
                </div>
              )}
              {data.flows.otherIn > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Other in (gift cards, bonus transfers)</span>
                  <span className="font-medium text-teal-700 tabular-nums">+{fmt(data.flows.otherIn, currency)}</span>
                </div>
              )}
              {data.flows.otherOut > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Moved into gift cards</span>
                  <span className="font-medium text-red-600 tabular-nums">-{fmt(data.flows.otherOut, currency)}</span>
                </div>
              )}
              {(() => {
                const f = data.flows;
                const net = f.recharged + f.refunded + f.otherIn - f.spent - f.otherOut;
                return (
                  <div className="flex justify-between pt-2 border-t border-gray-100">
                    <span className="font-medium text-gray-800">Net change in this period</span>
                    <span className={`font-semibold tabular-nums ${net >= 0 ? "text-gray-900" : "text-red-600"}`}>
                      {net >= 0 ? "+" : "-"}{fmt(Math.abs(net), currency)}
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Unspent balances right now: goods the platform still owes */}
          <div className="bg-amber-50 rounded-xl border border-amber-200 p-5 shadow-sm">
            <p className="text-xs text-amber-800 font-medium uppercase tracking-wide m-0 mb-3">
              Still owed to customers · right now
            </p>
            <p className="text-3xl font-bold text-amber-700 m-0 tabular-nums">{fmt(data.outstanding.total, currency)}</p>
            <p className="text-sm text-amber-900 mt-1 mb-3">The platform owes this value in orders.</p>
            <div className="space-y-1 text-sm text-amber-900">
              <div className="flex justify-between">
                <span>Unspent wallet money ({data.outstanding.walletCustomers} customers)</span>
                <span className="tabular-nums">{fmt(data.outstanding.wallet, currency)}</span>
              </div>
              <div className="flex justify-between">
                <span>Unredeemed gift cards ({data.outstanding.giftCardCount})</span>
                <span className="tabular-nums">{fmt(data.outstanding.giftCards, currency)}</span>
              </div>
            </div>
            {data.outstanding.expiredGiftCards > 0 && (
              <p className="text-xs text-amber-800 mt-3 mb-0">
                {fmt(data.outstanding.expiredGiftCards, currency)} in {data.outstanding.expiredGiftCardCount} expired,
                unredeemed gift cards is no longer owed.
              </p>
            )}
            {data.outstanding.bonus > 0 && (
              <p className="text-xs text-amber-800 mt-3 mb-0">
                Plus {fmt(data.outstanding.bonus, currency)} unspent in bonus wallets (loyalty, gift cards). Customers
                did not pay for this.
              </p>
            )}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading && !data ? (
          <div className="p-12 text-center text-gray-400">Loading...</div>
        ) : view === "giftcards" ? (
          !data?.giftCards || data.giftCards.length === 0 ? (
            <div className="p-12 text-center text-gray-400">
              {loading ? "Loading..." : "No gift cards bought in this period"}
            </div>
          ) : (
            <div className={`overflow-x-auto ${loading ? "opacity-60" : ""}`}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50 text-gray-500">
                    <th className="text-left px-6 py-3 font-medium">Bought</th>
                    <th className="text-left px-6 py-3 font-medium">Buyer</th>
                    <th className="text-left px-6 py-3 font-medium">Paid with</th>
                    <th className="text-left px-6 py-3 font-medium">Status</th>
                    <th className="text-left px-6 py-3 font-medium">Redeemed by</th>
                    <th className="text-right px-6 py-3 font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.giftCards.map((c) => (
                    <tr key={c._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-gray-500 whitespace-nowrap">{fmtDate(c.createdAt)}</td>
                      <td className="px-6 py-4">{personLine(c.buyer)}</td>
                      <td className="px-6 py-4 text-gray-700">
                        <p className="m-0">{c.paidWith}</p>
                        {c.transactionId && <p className="m-0 text-xs text-gray-400">Txn: {c.transactionId}</p>}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${GIFT_STATUS[c.status].className}`}
                        >
                          {GIFT_STATUS[c.status].label}
                        </span>
                        <p className="m-0 mt-1 text-xs text-gray-500">
                          {c.status === "redeemed" && c.redeemedAt
                            ? `on ${fmtDate(c.redeemedAt)}`
                            : `${c.status === "expired" ? "expired" : "expires"} ${fmtDate(c.expiresAt)}`}
                        </p>
                      </td>
                      <td className="px-6 py-4">{c.status === "redeemed" ? personLine(c.redeemer) : <span className="text-gray-400">—</span>}</td>
                      <td className="px-6 py-4 text-right font-medium tabular-nums text-gray-900 whitespace-nowrap">
                        {fmt(c.amount, currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : !data || data.entries.length === 0 ? (
          <div className="p-12 text-center text-gray-400">No wallet entries for this period</div>
        ) : (
          <>
            <div className={`overflow-x-auto ${loading ? "opacity-60" : ""}`}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50 text-gray-500">
                    <th className="text-left px-6 py-3 font-medium">Date</th>
                    <th className="text-left px-6 py-3 font-medium">Customer</th>
                    <th className="text-left px-6 py-3 font-medium">Activity</th>
                    <th className="text-left px-6 py-3 font-medium">Where</th>
                    <th className="text-right px-6 py-3 font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.entries.map((e) => (
                    <tr key={e._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-gray-500 whitespace-nowrap">{fmtDate(e.createdAt)}</td>
                      <td className="px-6 py-4">
                        {e.customer ? (
                          <>
                            <p className="m-0 font-medium text-gray-900">{e.customer.name || "—"}</p>
                            <p className="m-0 text-xs text-gray-500">
                              {[e.customer.phonenumber, e.customer.email].filter(Boolean).join(" · ")}
                            </p>
                          </>
                        ) : (
                          <span className="text-gray-400">Unknown</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <p className="m-0 text-gray-800">{e.activity}</p>
                        {e.description && <p className="m-0 text-xs text-gray-500">{e.description}</p>}
                        {e.transactionId && (
                          <p className="m-0 text-xs text-gray-400">Txn: {e.transactionId}</p>
                        )}
                      </td>
                      <td className="px-6 py-4 text-gray-700">
                        <p className="m-0">{e.location || "—"}</p>
                        {e.orderNumber && <p className="m-0 text-xs text-gray-500">Order #{e.orderNumber}</p>}
                      </td>
                      <td className="px-6 py-4 text-right whitespace-nowrap">
                        <span
                          className={`font-medium tabular-nums ${e.type === "credit" ? "text-teal-700" : "text-red-600"}`}
                        >
                          {`${e.type === "credit" ? "+" : "-"}${fmt(e.amount, currency)}`}
                        </span>
                        {e.balanceBefore != null && e.balanceAfter != null && (
                          <p className="m-0 mt-1 text-xs text-gray-500 tabular-nums">
                            Wallet: {fmt(e.balanceBefore, currency)} → {fmt(e.balanceAfter, currency)}
                          </p>
                        )}
                        {e.walletType && e.customer?.walletBalance != null && (
                          <p className="m-0 text-xs text-gray-400 tabular-nums">
                            Now: {fmt(e.customer.walletBalance, currency)}
                          </p>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {data && data.pages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
            <p className="text-sm text-gray-500">
              Page {data.page} of {data.pages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={data.page <= 1 || loading}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
                disabled={data.page >= data.pages || loading}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/** Wallet Recharge Report on its own page (/admin/ledger/wallet-recharges). */
export const WalletRechargePage: React.FC = () => {
  const navigate = useNavigate();
  // Platform-wide wallet data — admins only (the API refuses location admins too)
  if (getTokenInfo()?.role === "location_admin") {
    return <div className="p-6 text-sm text-gray-500">This report is only available to admins.</div>;
  }
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/admin/ledger")}
          className="p-2 hover:bg-gray-100 rounded-lg"
          aria-label="Back to ledger"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 m-0">Wallet Report</h1>
          <p className="text-sm text-gray-500 mt-1">
            Money customers added, what they spent, and what is still owed · all locations
          </p>
        </div>
      </div>
      <WalletRechargeReport />
    </div>
  );
};

// ─── Recent entries across all restaurants ───────────────────────────────────

const RECENT_PAGE_SIZE = 10;

const RecentEntries: React.FC = () => {
  const { currency } = useAuth();
  // Customer details modal opened from a row (same as a location's ledger)
  const [viewUser, setViewUser] = useState<LoyaltyCustomer | null>(null);
  const [openingUserId, setOpeningUserId] = useState<string | null>(null);
  const openUser = async (userId: string) => {
    try {
      setOpeningUserId(userId);
      const res = await api.get(`/loyalty/users/by-id/${userId}`);
      if (res.data.success) setViewUser(res.data.data);
    } catch {
      toast.error("Failed to load customer");
    } finally {
      setOpeningUserId(null);
    }
  };
  // Local calendar date (toISOString would give the UTC date, which can be a day off)
  const toLocalDateStr = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return toLocalDateStr(d);
  });
  const [endDate, setEndDate] = useState(() => toLocalDateStr(new Date()));
  const [typeFilter, setTypeFilter] = useState("");
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState<{
    entries: LedgerEntry[];
    total: number;
    page: number;
    pages: number;
    periodSummary?: {
      orderRevenue: number;
      orderCount: number;
      totalDebit: number;
      totalCredit: number;
      bonusesIssued?: number;
      bonusesEarned?: number;
      settled?: number;
      balance: number;
    };
  } | null>(null);

  const filterParams = useCallback(() => {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (typeFilter) params.type = typeFilter;
    return params;
  }, [startDate, endDate, typeFilter]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/ledger/recent", { params: { ...filterParams(), page, limit: RECENT_PAGE_SIZE } })
      .then((res) => {
        if (!cancelled && res.data.success) setData(res.data.data);
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load recent entries");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, filterParams, reloadKey]);

  const handleApply = () => {
    setPage(1);
    setReloadKey((k) => k + 1);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      await downloadFile(
        "/ledger/restaurant/all/export",
        filterParams(),
        `ledger-All-Locations-${startDate || "all"}-to-${endDate || "today"}.xlsx`
      );
    } catch {
      toast.error("Failed to export ledger");
    } finally {
      setExporting(false);
    }
  };

  const ps = data?.periodSummary;

  return (
    <div className="space-y-4">
      {viewUser && (
        <UserDetailsModal
          user={viewUser}
          onClose={() => setViewUser(null)}
          onBalanceChange={() => setReloadKey((k) => k + 1)}
        />
      )}
      {/* Filters (same as a location's ledger), across all locations */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="order">Orders</option>
              <option value="bonus">Bonuses</option>
              <option value="settlement">Settlements</option>
              <option value="adjustment">Adjustments</option>
              <option value="wallet_spend">Wallet Payments</option>
              <option value="order_cancelled">Cancellations</option>
            </select>
          </div>
          <button
            onClick={handleApply}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Apply
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            {exporting ? "Exporting..." : "Download Excel"}
          </button>
        </div>

        {/* Period summary, all locations */}
        {ps && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-6 text-sm">
            <span className="text-gray-500">
              Orders: <span className="font-semibold text-blue-700">{fmt(ps.orderRevenue, currency)}</span>
              <span className="text-gray-400"> ({ps.orderCount})</span>
            </span>
            <span className="text-gray-500">
              Bonuses earned: <span className="font-semibold text-indigo-600">{fmt(ps.bonusesEarned ?? 0, currency)}</span>
            </span>
            <span className="text-gray-500">
              Bonuses issued: <span className="font-semibold text-purple-600">{fmt(ps.bonusesIssued ?? 0, currency)}</span>
            </span>
            <span className="text-gray-500">
              Net owed:{" "}
              <span className={`font-semibold ${balanceIsBad(ps.balance) ? "text-red-600" : "text-green-600"}`}>
                {fmt(ps.balance, currency)}
              </span>
            </span>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-gray-800">Admin Ledger</h2>
          {data && data.total > 0 && (
            <span className="text-xs text-gray-400">{data.total} entries</span>
          )}
        </div>
        {loading && !data ? (
          <div className="p-12 text-center text-gray-400">Loading...</div>
        ) : !data || data.entries.length === 0 ? (
          <div className="p-12 text-center text-gray-400">No ledger entries for this period</div>
        ) : (
          <>
            <div className={`overflow-x-auto ${loading ? "opacity-60" : ""}`}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left px-6 py-3 font-medium text-gray-500">Date</th>
                    <th className="text-left px-6 py-3 font-medium text-gray-500">Location</th>
                    <th className="text-left px-6 py-3 font-medium text-gray-500">Type</th>
                    <th className="text-left px-6 py-3 font-medium text-gray-500">Description</th>
                    <th className="text-right px-6 py-3 font-medium text-gray-500">Amount</th>
                    <th className="text-right px-6 py-3 font-medium text-gray-500">Direction</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.entries.map((entry) => (
                    <LedgerEntryRow
                      key={entry._id}
                      entry={entry}
                      currency={currency}
                      showLocation
                      onOpenUser={openUser}
                      openingUserId={openingUserId}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            {data.pages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  Page {data.page} of {data.pages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={data.page <= 1 || loading}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
                    disabled={data.page >= data.pages || loading}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ─── Adjustment Modal ─────────────────────────────────────────────────────────

const AdjustmentModal: React.FC<{
  restaurantId: string;
  hotelName: string;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ restaurantId, hotelName, onClose, onSuccess }) => {
  const [amount, setAmount] = useState("");
  const [direction, setDirection] = useState<"debit" | "credit">("debit");
  const [description, setDescription] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (!description.trim()) { toast.error("Description is required"); return; }
    try {
      setSaving(true);
      await api.post(`/ledger/restaurant/${restaurantId}/adjustment`, {
        amount: amt,
        direction,
        description: description.trim(),
        adminNote: note || undefined,
      });
      toast.success("Adjustment recorded");
      onSuccess();
      onClose();
    } catch {
      toast.error("Failed to record adjustment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-bold text-gray-900">Manual Adjustment — {hotelName}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setDirection("debit")}
              className={`flex-1 py-2 rounded-lg text-sm font-medium border ${
                direction === "debit"
                  ? "bg-green-600 text-white border-green-600"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              Pay to Restaurant
            </button>
            <button
              type="button"
              onClick={() => setDirection("credit")}
              className={`flex-1 py-2 rounded-lg text-sm font-medium border ${
                direction === "credit"
                  ? "bg-red-600 text-white border-red-600"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              Paid by Restaurant
            </button>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
            <input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Reason for adjustment" required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</label>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {saving ? "Saving..." : "Record Adjustment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Restaurant Detail View ───────────────────────────────────────────────────

const RestaurantLedgerView: React.FC<{
  restaurant: RestaurantSummary;
  onBack?: () => void;
  // Location admins can review their ledger but not settle or adjust it
  readOnly?: boolean;
}> = ({ restaurant, onBack, readOnly = false }) => {
  const { currency } = useAuth();
  const [ledger, setLedger] = useState<LedgerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState("");
  // Local calendar date (toISOString would give the UTC date, which can be a day off)
  const toLocalDateStr = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return toLocalDateStr(d);
  });
  const [endDate, setEndDate] = useState(() => toLocalDateStr(new Date()));
  const [showAdjustment, setShowAdjustment] = useState(false);
  // Customer details modal opened from a ledger row
  const [viewUser, setViewUser] = useState<LoyaltyCustomer | null>(null);
  const [openingUserId, setOpeningUserId] = useState<string | null>(null);

  const openUser = async (userId: string) => {
    try {
      setOpeningUserId(userId);
      const res = await api.get(`/loyalty/users/by-id/${userId}`);
      if (res.data.success) setViewUser(res.data.data);
    } catch {
      toast.error("Failed to load customer");
    } finally {
      setOpeningUserId(null);
    }
  };
  const [allTimeSummary, setAllTimeSummary] = useState<LedgerData["allTimeSummary"] | null>(null);

  const fetchLedger = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = { page: String(page), limit: "50" };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (typeFilter) params.type = typeFilter;

      const res = await api.get(`/ledger/restaurant/${restaurant.restaurantId}`, { params });
      if (res.data.success) {
        setLedger(res.data.data);
        setAllTimeSummary(res.data.data.allTimeSummary);
      }
    } catch {
      toast.error("Failed to load ledger");
    } finally {
      setLoading(false);
    }
  }, [restaurant.restaurantId, page, startDate, endDate, typeFilter]);

  useEffect(() => { fetchLedger(); }, [fetchLedger]);

  const handleRefresh = () => { setPage(1); fetchLedger(); };

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    try {
      setExporting(true);
      const params: Record<string, string> = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (typeFilter) params.type = typeFilter;

      await downloadFile(
        `/ledger/restaurant/${restaurant.restaurantId}/export`,
        params,
        `ledger-${startDate || "all"}-to-${endDate || "today"}.xlsx`
      );
    } catch {
      toast.error("Failed to export ledger");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {showAdjustment && (
        <AdjustmentModal
          restaurantId={restaurant.restaurantId}
          hotelName={restaurant.hotelName}
          onClose={() => setShowAdjustment(false)}
          onSuccess={handleRefresh}
        />
      )}

      {viewUser && (
        <UserDetailsModal
          user={viewUser}
          onClose={() => setViewUser(null)}
          readOnly={readOnly}
          onBalanceChange={() => fetchLedger()}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          {onBack && (
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
          )}
          <div>
            <h1 className="text-xl font-bold text-gray-900">{restaurant.hotelName}</h1>
            <p className="text-xs text-gray-500">{restaurant.address}</p>
          </div>
        </div>
        {!readOnly && (
        <div className="flex gap-2">
          <button
            onClick={() => setShowAdjustment(true)}
            className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium flex items-center gap-2"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Adjust
          </button>
        </div>
        )}
      </div>

      {/* All-time balance cards */}
      {allTimeSummary && (
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${readOnly ? "lg:grid-cols-3" : "lg:grid-cols-4"} gap-4`}>
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-blue-500" />
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide m-0">Order Revenue</p>
            </div>
            <p className="text-2xl font-bold text-blue-700">{fmt(allTimeSummary.orderRevenue, currency)}</p>
            <p className="text-xs text-gray-400 mt-1">{allTimeSummary.orderCount} orders</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-indigo-500" />
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide m-0">Bonuses Earned</p>
            </div>
            <p className="text-2xl font-bold text-indigo-600">{fmt(allTimeSummary.bonusesEarned ?? allTimeSummary.loyaltyDebits, currency)}</p>
            <p className="text-xs text-gray-400 mt-1">Loyalty on orders</p>
          </div>
          {/* Bonuses issued by admins are admin-only */}
          {!readOnly && (
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-purple-500" />
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide m-0">Bonuses Issued</p>
              </div>
              <p className="text-2xl font-bold text-purple-600">{fmt(allTimeSummary.bonusesIssued ?? 0, currency)}</p>
              <p className="text-xs text-gray-400 mt-1">Compensation</p>
            </div>
          )}
          <div
            className={`rounded-xl border p-5 shadow-sm ${
              balanceIsBad(allTimeSummary.balance) ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className={`w-4 h-4 ${balanceIsBad(allTimeSummary.balance) ? "text-red-500" : "text-green-500"}`} />
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 m-0">
                {balanceTitle(allTimeSummary.balance)}
              </p>
            </div>
            <p className={`text-2xl font-bold ${balanceIsBad(allTimeSummary.balance) ? "text-red-700" : "text-green-700"}`}>
              {allTimeSummary.balance > 0 ? "Owes " : allTimeSummary.balance < 0 ? "Credit " : ""}
              {fmt(Math.abs(allTimeSummary.balance), currency)}
            </p>
            {allTimeSummary.balance > 0 && (
              <p className={`text-xs mt-1 flex items-center gap-1 ${balanceIsBad(allTimeSummary.balance) ? "text-red-500" : "text-green-600"}`}>
                <AlertCircle className="w-3 h-3" /> Payment pending
              </p>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="order">Orders</option>
              <option value="bonus">Bonuses</option>
              <option value="settlement">Settlements</option>
              <option value="adjustment">Adjustments</option>
              <option value="wallet_spend">Wallet Payments</option>
              <option value="order_cancelled">Cancellations</option>
            </select>
          </div>
          <button
            onClick={handleRefresh}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Apply
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            {exporting ? "Exporting..." : "Download Excel"}
          </button>
        </div>

        {/* Period summary */}
        {ledger && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-6 text-sm">
            <span className="text-gray-500">
              Orders: <span className="font-semibold text-blue-700">{fmt(ledger.periodSummary.orderRevenue, currency)}</span>
              <span className="text-gray-400"> ({ledger.periodSummary.orderCount})</span>
            </span>
            <span className="text-gray-500">
              Bonuses earned: <span className="font-semibold text-indigo-600">{fmt(ledger.periodSummary.bonusesEarned ?? ledger.periodSummary.loyaltyDebits, currency)}</span>
            </span>
            {!readOnly && (
              <span className="text-gray-500">
                Bonuses issued: <span className="font-semibold text-purple-600">{fmt(ledger.periodSummary.bonusesIssued ?? 0, currency)}</span>
              </span>
            )}
            <span className="text-gray-500">
              Net owed: <span className={`font-semibold ${balanceIsBad(ledger.periodSummary.balance) ? "text-red-600" : "text-green-600"}`}>
                {fmt(ledger.periodSummary.balance, currency)}
              </span>
            </span>
          </div>
        )}
      </div>

      {/* Transaction table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-800">Transactions</h2>
        </div>
        {loading ? (
          <div className="p-12 text-center text-gray-400">Loading...</div>
        ) : !ledger || ledger.entries.length === 0 ? (
          <div className="p-12 text-center text-gray-400">No transactions for this period</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left px-6 py-3 font-medium text-gray-500">Date</th>
                    <th className="text-left px-6 py-3 font-medium text-gray-500">Type</th>
                    <th className="text-left px-6 py-3 font-medium text-gray-500">Description</th>
                    <th className="text-right px-6 py-3 font-medium text-gray-500">Amount</th>
                    <th className="text-right px-6 py-3 font-medium text-gray-500">Direction</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {ledger.entries.map((entry) => (
                    <LedgerEntryRow
                      key={entry._id}
                      entry={entry}
                      currency={currency}
                      onOpenUser={openUser}
                      openingUserId={openingUserId}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {ledger.pages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  Page {ledger.page} of {ledger.pages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={ledger.page <= 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(ledger.pages, p + 1))}
                    disabled={ledger.page >= ledger.pages}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-40 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ─── Main Ledger Page ─────────────────────────────────────────────────────────

const Ledger: React.FC = () => {
  const [summaries, setSummaries] = useState<RestaurantSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRestaurant, setSelectedRestaurant] = useState<RestaurantSummary | null>(null);

  const fetchSummaries = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/ledger/summary");
      if (res.data.success) setSummaries(res.data.data);
    } catch {
      toast.error("Failed to load restaurants");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSummaries(); }, [fetchSummaries]);

  // Location admins go straight to their own location's ledger, read-only
  const isLocationAdmin = getTokenInfo()?.role === "location_admin";
  if (isLocationAdmin) {
    if (loading) {
      return <div className="p-6 text-sm text-gray-500">Loading ledger…</div>;
    }
    if (!summaries[0]) {
      return <div className="p-6 text-sm text-gray-500">No ledger found for your location.</div>;
    }
    return <RestaurantLedgerView restaurant={summaries[0]} readOnly />;
  }

  if (selectedRestaurant) {
    return (
      <RestaurantLedgerView
        restaurant={selectedRestaurant}
        onBack={() => { setSelectedRestaurant(null); fetchSummaries(); }}
      />
    );
  }

  return (
    <SummaryView
      summaries={summaries}
      loading={loading}
      onSelect={setSelectedRestaurant}
      onRefresh={fetchSummaries}
    />
  );
};

export default Ledger;
